import QuillDelta from 'quill-delta';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks/useFeature';
import { useLocalize } from '@treams/treams-hooks/useLocalize';
import { DirectionsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconButton } from '@treams/treams-ui/common/button/IconButton';
import { Popover } from '@treams/treams-ui/common/Popover';
import { Separator } from '@treams/treams-ui/common/Separator';
import { Paragraph } from '@treams/treams-ui/common/Text';
import { AddIcon } from '@treams/treams-ui/icons';
import { tokens } from '@treams/treams-ui/theme/tokens';

import CreateModal from './CreateModal';
import CompetencyBlockIcon from './icons/CompetencyBlockIcon';
import FinalScoreBlockIcon from './icons/FinalScoreBlockIcon';
import MultipleChoiceBlockIcon from './icons/MultipleChoiceBlockIcon';
import OpenQuestionBlockIcon from './icons/OpenQuestionBlockIcon';
import ParagraphBlockIcon from './icons/ParagraphBlockIcon';
import ReviewGoalsBlockIcon from './icons/ReviewGoalsBlockIcon';
import SentimentBlockIcon from './icons/SentimentBlockIcon';
import SetGoalsBlockIcon from './icons/SetGoalsBlockIcon';
import SignatureBlockIcon from './icons/SignatureBlockIcon';
import StarRatingBlockIcon from './icons/StarRatingBlockIcon';
import TitleBlockIcon from './icons/TitleBlockIcon';

const Container = styled.div`
	padding: ${tokens.padding.container}px 0;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 70px 70px 70px 70px;
	gap: 10px;
`;

const Block = styled(Flex)`
	background: #f1f3f5;
	border-radius: 6px;
	height: 70px;
	cursor: pointer;

	&:hover {
		background: #edeeef;
	}

	&:active {
		background: #e1e2e3;
	}
`;

const BlockTitle = styled.div`
	text-align: center;
	color: ${tokens.text.secondary};
	font-size: 10px;
	margin-top: 5px;
	line-height: 12px;
`;

function BlockButton(props: {
	example: graphql.CreateEvaluationSectionItemInput;
	icon: React.ReactNode;
	title: string;
	onCreate: (item: graphql.CreateEvaluationSectionItemInput) => void;
}) {
	return (
		<Block
			role="button"
			tabIndex={0}
			aria-label={props.title}
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			onClick={() => props.onCreate(props.example)}
		>
			{props.icon}
			<BlockTitle>{props.title}</BlockTitle>
		</Block>
	);
}

export default function AddButton(props: {
	existingTypes: graphql.EvaluationSectionItemType[];
	onCreate: (item: graphql.CreateEvaluationSectionItemInput) => void;
}) {
	const { t } = useTranslation();

	const [active, setActive] = useState(false);
	const [item, setItem] = useState<graphql.EvaluationSectionItem | null>(null);

	const handleCreate = (item: graphql.CreateEvaluationSectionItemInput) => {
		setItem(item as unknown as graphql.EvaluationSectionItem);
	};

	const usesDevelopmentPlan = useFeature(graphql.FeatureName.DevelopmentPlan);
	const reviewGoals = useFeature(graphql.FeatureName.Goals);
	const multipleChoice = useFeature(graphql.FeatureName.ConversationMultipleChoice);

	const { language } = useLocalize();

	return (
		<Container>
			<Popover.Target
				visible={active}
				contents={
					<Box p={3}>
						{item && (
							<CreateModal
								item={item}
								onCreate={(item) => {
									props.onCreate(item as graphql.CreateEvaluationSectionItemInput);
									setItem(null);
									setActive(false);
								}}
								onCancel={() => {
									setItem(null);
								}}
							/>
						)}

						{!item && (
							<>
								<Grid>
									<BlockButton
										title={t('common.settings.evaluationForms.stars.title')}
										icon={<StarRatingBlockIcon />}
										onCreate={handleCreate}
										example={{
											type: graphql.EvaluationSectionItemType.Stars,
											fillInBy: graphql.EvaluationFillInBy.Both,
											stars: {
												title: [
													{
														languageID: language.id,
														text: '',
													},
												],
											},
										}}
									/>
									{multipleChoice ? (
										<BlockButton
											title={t('common.settings.evaluationForms.multipleChoice.title')}
											icon={<MultipleChoiceBlockIcon />}
											onCreate={handleCreate}
											example={{
												type: graphql.EvaluationSectionItemType.MultipleChoice,
												fillInBy: graphql.EvaluationFillInBy.Both,
												multipleChoice: {
													title: [
														{
															languageID: language.id,
															text: '',
														},
													],
													optionsMethod: graphql.EvaluationMultipleChoiceOptionsMethod.Single,
													options: [
														{
															text: [
																{
																	languageID: language.id,
																	text: '',
																},
															],
														},
														{
															text: [
																{
																	languageID: language.id,
																	text: '',
																},
															],
														},
													],
												},
											}}
										/>
									) : null}
									<BlockButton
										title={t('common.settings.evaluationForms.openQuestion.title')}
										icon={<OpenQuestionBlockIcon />}
										onCreate={handleCreate}
										example={{
											type: graphql.EvaluationSectionItemType.OpenQuestion,
											fillInBy: graphql.EvaluationFillInBy.Both,
											openQuestion: {
												title: [
													{
														languageID: language.id,
														text: '',
													},
												],
											},
										}}
									/>
									<BlockButton
										title={t('common.settings.evaluationForms.competency.title')}
										icon={<CompetencyBlockIcon />}
										onCreate={handleCreate}
										example={{
											type: graphql.EvaluationSectionItemType.Competency,
											fillInBy: graphql.EvaluationFillInBy.Both,
											competency: {
												competency: {
													title: [
														{
															languageID: language.id,
															text: '',
														},
													],
												},
												weight: 1,
											} as unknown as graphql.EvaluationCompetencyItemInput,
										}}
									/>
									<BlockButton
										title={t('common.settings.evaluationForms.sentiment.title')}
										icon={<SentimentBlockIcon />}
										onCreate={handleCreate}
										example={{
											type: graphql.EvaluationSectionItemType.Sentiment,
											fillInBy: graphql.EvaluationFillInBy.Both,
											sentiment: {
												title: [
													{
														languageID: language.id,
														text: '',
													},
												],
												displayMethod: graphql.EvaluationSentimentDisplayMethod.Emoji,
											},
										}}
									/>
									<BlockButton
										title={t('common.settings.evaluationForms.title.title')}
										icon={<TitleBlockIcon />}
										onCreate={handleCreate}
										example={{
											type: graphql.EvaluationSectionItemType.Title,
											fillInBy: graphql.EvaluationFillInBy.Both,
											title: {
												text: [
													{
														languageID: language.id,
														text: '',
													},
												],
											},
										}}
									/>
									<BlockButton
										title={t('common.settings.evaluationForms.paragraph.title')}
										icon={<ParagraphBlockIcon />}
										onCreate={handleCreate}
										example={{
											type: graphql.EvaluationSectionItemType.Paragraph,
											fillInBy: graphql.EvaluationFillInBy.Both,
											paragraph: {
												text: [
													{
														languageID: language.id,
														text: {
															ops: [{ insert: '' }],
														} as QuillDelta,
													},
												],
											},
										}}
									/>
								</Grid>

								{!props.existingTypes.includes(graphql.EvaluationSectionItemType.ReviewGoals) ||
								!props.existingTypes.includes(graphql.EvaluationSectionItemType.FinalScore) ||
								!props.existingTypes.includes(graphql.EvaluationSectionItemType.Signature) ? (
									<Box mb={2} mt={3}>
										<Paragraph>
											{t('common.settings.evaluationForms.formalActions.title')}
										</Paragraph>
										<Separator />
									</Box>
								) : null}

								<Grid>
									{!reviewGoals ||
									props.existingTypes.includes(
										graphql.EvaluationSectionItemType.ReviewGoals,
									) ? null : (
										<BlockButton
											title={t('common.settings.evaluationForms.reviewGoals.title')}
											icon={<ReviewGoalsBlockIcon />}
											onCreate={handleCreate}
											example={{
												type: graphql.EvaluationSectionItemType.ReviewGoals,
												fillInBy: graphql.EvaluationFillInBy.Both,
												reviewGoals: {
													competencyLevelSetID: undefined as unknown as string,
												},
											}}
										/>
									)}
									{props.existingTypes.includes(
										graphql.EvaluationSectionItemType.SetGoals,
									) ? null : (
										<BlockButton
											title={t('common.settings.evaluationForms.setGoals.title')}
											icon={<SetGoalsBlockIcon />}
											onCreate={handleCreate}
											example={{
												type: graphql.EvaluationSectionItemType.SetGoals,
												fillInBy: graphql.EvaluationFillInBy.Employee,
												setGoals: {
													title: [
														{
															languageID: language.id,
															text: t('common.settings.evaluationForms.setGoals.defaultTitle'),
														},
													],
												},
											}}
										/>
									)}
									{props.existingTypes.includes(
										graphql.EvaluationSectionItemType.FinalScore,
									) ? null : (
										<BlockButton
											title={t('common.settings.evaluationForms.finalScore.title')}
											icon={<FinalScoreBlockIcon />}
											onCreate={handleCreate}
											example={{
												type: graphql.EvaluationSectionItemType.FinalScore,
												fillInBy: graphql.EvaluationFillInBy.Both,
												finalScore: {
													calculationMethod: graphql.EvaluationFinalScoreCalculationMethod.Average,
													title: [
														{
															languageID: language.id,
															text: '',
														},
													],
													competencyLevelSetID: undefined as unknown as string,
												},
											}}
										/>
									)}
									{props.existingTypes.includes(
										graphql.EvaluationSectionItemType.Signature,
									) ? null : (
										<BlockButton
											title={t('common.settings.evaluationForms.signature.title')}
											icon={<SignatureBlockIcon />}
											onCreate={handleCreate}
											example={{
												type: graphql.EvaluationSectionItemType.Signature,
												fillInBy: graphql.EvaluationFillInBy.Both,
												signature: {
													title: [
														{
															languageID: language.id,
															text: '',
														},
													],
												},
											}}
										/>
									)}
									{!usesDevelopmentPlan ||
									props.existingTypes.includes(
										graphql.EvaluationSectionItemType.DevelopmentPlan,
									) ? null : (
										<BlockButton
											title={t('common.settings.evaluationForms.developmentPlan.title')}
											icon={<DirectionsIcon />}
											onCreate={handleCreate}
											example={{
												type: graphql.EvaluationSectionItemType.DevelopmentPlan,
												fillInBy: graphql.EvaluationFillInBy.Employee,
												developmentPlan: {
													title: [
														{
															languageID: language.id,
															text: t(
																'common.settings.evaluationForms.developmentPlan.defaultTitle',
															),
														},
													],
												},
											}}
										/>
									)}
								</Grid>
							</>
						)}
					</Box>
				}
				onDismiss={() => setActive(false)}
			>
				<IconButton
					type="secondary"
					icon={<AddIcon />}
					onClick={() => setActive(true)}
					aria-label={t('common.settings.evaluationForms.addBlock')}
				/>
			</Popover.Target>
		</Container>
	);
}
